<template>
  <page-wrapper sticky-second-row error-key="users" :transparent-bg="isGridView">
    <template #title>
      <div class="d-flex justify-space-between align-center w-100">
        <div class="h3">Пользователи</div>

        <view-mode-toggle v-if="$vuetify.display.mdAndUp" v-model="viewMode" />
      </div>
    </template>

    <template #secondRow>
      <user-request-card v-for="item in accessRequests" :key="item.id" :data="item" class="mb-4" />
      <div class="d-flex">
        <users-filters v-model="filters" :role-filter-items="roleFilterItems" />
        <v-spacer />
        <invite-users-dialog @submit="getData" />
      </div>
    </template>

    <e-data-table
      v-if="viewMode === ViewMode.Table && $vuetify.display.mdAndUp"
      class="mt-2"
      interactive
      :headers="headers"
      :items="filteredUserItems"
      :loading="isLoading"
      :sticky-header-top="$vuetify.display.lgAndDown ? '64px' : '68px'"
      :row-props="tableRowProps"
      @on-row-click="rowClickHandler"
    >
      <template v-slot:[`header.literal`]>
        <e-icon name="gallery" />
      </template>

      <template v-slot:[`item.literal`]="{ value, item }">
        <div>
          <avatar :avatar="item.avatar" :literal="value" :disabled="item.isInvitation" size="L" />
        </div>
      </template>
      <template v-slot:[`item.name`]="{ item }">
        <div>{{ item.lastName }} {{ item.name }}</div>
      </template>
      <template v-slot:[`item.role`]="{ item }">
        <e-select
          placeholder="Выберите роль"
          data-table-clickable
          item-value="value"
          :model-value="item.role"
          :items="USER_ROLE_SELECT_ITEMS"
          :disabled="item.isMe || item.role === Roles.Support || item.role === Roles.Owner"
          :loading="isLoadingUserRoleUpdateId === item.id"
          @click.stop
          @update:model-value="handleUserRoleUpdate(item, $event)"
        />
      </template>
      <template v-slot:[`item.status`]="{ item }">
        <div class="py-2">
          <status-string v-if="item.isInvitation" text="Приглашение отправлено" class="text-primary" />
          <status-string v-else text="Активен" class="text-green" />
        </div>
      </template>
      <template v-slot:[`item.actions`]="{ item }">
        <div v-if="!item.isMe && !item.isOwner && !item.isInvitation" data-table-clickable>
          <user-actions-menu
            :can-delete="!item.isMe && !item.isOwner"
            :user-info="{
              id: item.id,
              name: item.name,
              lastName: item.lastName,
              middleName: item.middleName,
            }"
            @edit="toProfile"
          />
        </div>
      </template>
    </e-data-table>

    <div v-else class="users__grid">
      <template v-if="isLoading">
        <skeleton-loader v-for="item in 10" height="200px" :key="item" />
      </template>
      <template v-else>
        <div
          v-for="item in filteredUserItems"
          :key="item.id"
          class="users__grid-item"
          @click="!item.isInvitation && toProfile(item.id)"
        >
          <div class="d-flex w-100">
            <div class="py-2">
              <status-string v-if="item.isInvitation" text="Приглашение отправлено" class="text-primary" />
              <status-string v-else text="Активен" class="text-green" />
            </div>
            <v-spacer />
            <user-actions-menu
              v-if="!item.isMe && !item.isOwner && !item.isInvitation"
              :can-delete="!item.isMe && !item.isOwner"
              :user-info="{
                id: item.id,
                name: item.name,
                lastName: item.lastName,
                middleName: item.middleName,
              }"
              @edit="toProfile"
            />
          </div>
          <avatar :avatar="item.avatar" :literal="item.literal" :disabled="item.isInvitation" size="XL" class="mt-3" />
          <div class="paragraph-s mt-7">
            <div v-if="item.isInvitation" class="link">{{ item.email }}</div>
            <b v-else>{{ item.lastName }} {{ item.name }}</b>
          </div>
          <e-select
            class="mt-3"
            placeholder="Выберите роль"
            item-value="value"
            :model-value="item.role"
            :items="USER_ROLE_SELECT_ITEMS"
            :disabled="item.isMe || item.role === Roles.Support || item.role === Roles.Owner"
            :loading="isLoadingUserRoleUpdateId === item.id"
            @click.stop
            @update:model-value="handleUserRoleUpdate(item, $event)"
          />
          <div class="paragraph-xs mt-10 text-ordinary">
            {{ item.email }}
          </div>
        </div>
      </template>
    </div>
  </page-wrapper>
</template>

<script lang="ts" setup>
import { computed, ref, toRef } from 'vue';
import { useRouter } from 'vue-router';
import { useDisplay } from 'vuetify';
import type { DataTableHeaders } from '@/core/types/components/table';
import type { ArrayElementType } from '@/core/types/gengeric';
import Avatar from '@/core/components/common/Avatar.vue';
import StatusString from '@/core/components/common/StatusString.vue';
import ViewModeToggle from '@/core/components/common/ViewModeToggle.vue';
import PageWrapper from '@/core/components/interface/PageWrapper.vue';
import SkeletonLoader from '@/core/components/interface/SkeletonLoader.vue';
import { EDataTable, EIcon, ESelect } from '@/core/components/ui';
import { useFetch } from '@/core/composables/useFetch';
import { useUserPreference } from '@/core/composables/useUserPreferences';
import { getConfigValue } from '@/core/consts/config';
import { RouteNames } from '@/core/plugins/router/routeNames';
import { ViewMode } from '@/core/types/components/helps';
import { useComputedValue } from '@/core/utils/reactivity';
import { stringUrlSerializer, useQueryValue } from '@/core/utils/router/query-value';
import { containsSubstring } from '@/core/utils/string';
import { useAuthStore } from '@/parts/auth/store/auth';
import InviteUsersDialog from '@/parts/users/components/InviteUsersDialog.vue';
import UserActionsMenu from '@/parts/users/components/UserActionsMenu.vue';
import UserRequestCard from '@/parts/users/components/UserRequestCard.vue';
import UsersFilters from '@/parts/users/components/UsersFilters.vue';
import { useUsersStore } from '@/parts/users/store/users';
import {
  defaultFilters,
  Roles,
  type RolesType,
  USER_ROLE_MAP,
  USER_ROLE_SELECT_ITEMS,
} from '@/parts/users/utils/consts';

const $router = useRouter();
const display = useDisplay();
const { profile } = useAuthStore();
const {
  getUsers,
  getUsersInvitations,
  users,
  invitations,
  setInvitationRole,
  setUserRole,
  getActiveAccessRequests,
  accessRequests,
} = useUsersStore();

const viewMode = useUserPreference('usersPageViewMode', ViewMode.Table);

const isGridView = toRef(() => viewMode.value === ViewMode.Grid || display.smAndDown.value);

const filters = ref(defaultFilters());

useQueryValue(useComputedValue(filters, 'searchString'), 'query', stringUrlSerializer);
useQueryValue(useComputedValue(filters, 'filterRole'), 'role', stringUrlSerializer);

const headers: DataTableHeaders = [
  {
    key: 'literal',
    sortable: false,
    width: display.mdAndDown.value ? 80 : 96,
  },
  {
    key: 'name',
    title: 'Имя',
    sortRaw: (v1, v2) => (`${v1.lastName} ${v1.name}` < `${v2.lastName} ${v2.name}` ? 1 : -1),
  },
  {
    key: 'email',
    title: 'Email',
  },
  {
    key: 'role',
    title: 'Роль',
  },
  {
    key: 'status',
    title: 'Статус',
    sortRaw: (v1, v2) => (v1.isInvitation === v2.isInvitation || v1.isInvitation < v2.isInvitation ? 1 : -1),
  },
  {
    key: 'actions',
    sortable: false,
    width: display.mdAndDown.value ? 40 : 70,
    align: 'end',
  },
];

type UserTableItem = {
  isInvitation: boolean;
  id: string;
  email: string;
  literal: string;
  name: string;
  lastName: string;
  middleName: string | null | undefined;
  avatar: {
    url?: string | null;
    canNotLoad: boolean;
  } | null;
  isOwner: boolean;
  isMe: boolean;
  role: RolesType | null | undefined;
};

const usersTableItems = computed<UserTableItem[]>(
  () =>
    users.value?.map((item) => ({
      isInvitation: false,
      id: item.id,
      email: item.email,
      literal: item.profile.name.slice(0, 2),
      name: item.profile.name,
      lastName: item.profile.lastName,
      middleName: item.profile.middleName,
      avatar: item.profile.userAvatar,
      isOwner: item.isOwner,
      isMe: profile.value?.id === item.id,
      role: item.isOwner ? Roles.Owner : item.role,
    })) || [],
);

const invitationsItems = computed<UserTableItem[]>(
  () =>
    invitations.value?.map((item) => ({
      isInvitation: true,
      id: item.id,
      email: item.email,
      literal: item.email.slice(0, 2),
      name: '',
      lastName: '',
      middleName: '',
      avatar: null,
      isOwner: false,
      isMe: false,
      role: item.role,
    })) || [],
);

const userItems = computed(() => [...usersTableItems.value, ...invitationsItems.value]);

const tableRowProps = (data: {
  item: UserTableItem;
}): {
  class: string;
} | void => {
  if (data.item.isInvitation) {
    return {
      class: 'inactive',
    };
  }
};

const filteredUserItems = computed(() => {
  return userItems.value.filter((item) => {
    if (filters.value.filterRole && item.role !== filters.value.filterRole) {
      return false;
    }

    if (
      filters.value.searchString &&
      !containsSubstring(item.name, filters.value.searchString) &&
      !containsSubstring(item.lastName, filters.value.searchString) &&
      !containsSubstring(item.email, filters.value.searchString)
    ) {
      return false;
    }

    return true;
  });
});

const roleFilterItems = computed(() =>
  userItems.value.reduce<
    {
      title: string;
      value: RolesType;
    }[]
  >((res, item) => {
    if (item.role && !res.some((x) => x.title === USER_ROLE_MAP[item.role!])) {
      res.push({ title: USER_ROLE_MAP[item.role!], value: item.role });
    }

    return res;
  }, []),
);

const isLoadingUserRoleUpdateId = ref<string>();

const handleUserRoleUpdate = async (
  user: ArrayElementType<typeof userItems.value>,
  role: RolesType | null | undefined,
) => {
  if (!role || role === Roles.Owner) {
    return;
  }

  isLoadingUserRoleUpdateId.value = user.id;

  if (user.isInvitation) {
    await setInvitationRole({ invitationId: user.id, role });
  } else {
    await setUserRole({ userId: user.id, role });
  }

  isLoadingUserRoleUpdateId.value = undefined;
};

const toProfile = (id: string) => {
  if (profile.value?.id === id) {
    $router.push({ name: RouteNames.Profile });
  } else {
    $router.push({ name: RouteNames.User, params: { id } });
  }
};

const rowClickHandler = (e: Event, row: { item: ArrayElementType<typeof userItems.value> }) => {
  if (row.item.isInvitation) return;

  toProfile(row.item.id);
};

const getData = async () => {
  await Promise.all([getUsers(), getUsersInvitations(), getActiveAccessRequests()]);
};

const { isLoading } = useFetch(async () => {
  await getData();
});
</script>

<style lang="scss" scoped>
.users {
  &__grid {
    display: grid;

    grid-gap: $gap-4;
    grid-template-columns: repeat(4, minmax(0, 1fr));

    @include lg-and-down {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    @include sm-and-down {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @include xs {
      grid-template-columns: repeat(1, minmax(0, 1fr));
    }

    &-item {
      display: flex;
      flex-direction: column;
      align-items: center;

      min-height: 316px;
      padding: 10px 10px 16px 16px;

      background-color: $main-bg-color;
      border-radius: $border-radius-l;

      cursor: pointer;

      @include hover {
        box-shadow: $border-box-shadow;
      }
    }
  }
}
</style>
